import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CityView, CountryView, StateView, UserView } from '@api-proxies';
import { CityService } from '@http/clients/city.service';
import { CountryService } from '@http/clients/country.service';
import { StateService } from '@http/clients/state.service';
import { UserService } from '@http/clients/user.service';
import { FilterStateService } from '@services/filter-state.service';
import { AdditionalFiltersComponent } from '@shared/additional-filters/additional-filters.component';
import { ClearFiltersButtonComponent } from '@shared/clear-filters-button/clear-filters-button.component';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { FilterService, IGetById, SearchInputComponent } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { BUILDINGS_FILTER_CONFIG, BUILDINGS_FILTER_KEY, IFilterValueBuildings } from './buildings-filter.config';

@Component({
    selector: 'app-buildings-filters',
    templateUrl: './buildings-filters.component.html',
    styleUrls: ['./buildings-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SearchInputComponent, ClearFiltersButtonComponent, CustomMenuItemComponent, AdditionalFiltersComponent]
})
export class BuildingsFiltersComponent implements OnInit, OnDestroy {
    filtersKey = BUILDINGS_FILTER_KEY;

    additionalFiltersConfig = BUILDINGS_FILTER_CONFIG;

    disabledClearFiltersBtn = false;

    filters: IFilterValueBuildings = {} as IFilterValueBuildings;

    public cityService = inject(CityService);
    public stateService = inject(StateService);
    public countryService = inject(CountryService);

    private filterSubscription?: Subscription;

    private readonly filterStateService = inject(FilterStateService<IFilterValueBuildings>);
    private readonly filterService = inject(FilterService);
    private readonly userService = inject(UserService);
    private readonly activatedRoute = inject(ActivatedRoute);

    async ngOnInit(): Promise<void> {
        await this.deserializeUrlParams();

        if (Object.keys(this.activatedRoute.snapshot.queryParams).length === 0) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(
                this.filtersKey
            ) as IFilterValueBuildings;

            this.filters = {
                ...this.filters,
                ...(filtersFromLocalStorage ? filtersFromLocalStorage : {})
            };
        }

        this.filterStateService.updateFilters(this.filters);

        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (filters) => {
            if (!filters) {
                this.filters = {} as IFilterValueBuildings;
            } else {
                this.filters = filters as IFilterValueBuildings;
                await this.updateFilterUrlParams();
                this.disabledClearFiltersBtn = !this.someFilterApplied();
            }
            this.filterStateService.saveToLocalStorage(this.filtersKey, this.filters);
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    onFilterChanged(): void {
        this.filterStateService.updateMainFilters(this.filters);
    }

    private someFilterApplied(): boolean {
        return Object.values(this.filters).some((f) =>
            Array.isArray(f) ? f.length > 0 : f !== null && f !== undefined && f !== ''
        );
    }

    private async updateFilterUrlParams(): Promise<void> {
        await this.filterService.serialize<IFilterValueBuildings>(this.filters, 'sortBy');
        await this.filterService.serialize<IFilterValueBuildings>(this.filters, 'sortDirection');
        await this.filterService.serialize<IFilterValueBuildings>(this.filters, 'searchText');
        await this.filterService.serializeTypeAhead<IFilterValueBuildings, CityView>('city', this.filters.city, 'id');
        await this.filterService.serializeTypeAhead<IFilterValueBuildings, StateView>(
            'state',
            this.filters.state,
            'id'
        );
        await this.filterService.serializeTypeAhead<IFilterValueBuildings, CountryView>(
            'country',
            this.filters.country,
            'id'
        );
        await this.filterService.serializeBoolean<IFilterValueBuildings>(this.filters, 'includeArchived');

        await this.filterService.serializeTypeAhead<IFilterValueBuildings, UserView>(
            'salespersons',
            this.filters.salespersons,
            'externalId',
            true
        );
    }

    private async deserializeUrlParams() {
        this.filterService.deserialize<IFilterValueBuildings>(this.filters, 'sortBy');
        this.filterService.deserialize<IFilterValueBuildings>(this.filters, 'sortDirection');
        this.filterService.deserialize<IFilterValueBuildings>(this.filters, 'searchText');
        await this.filterService.deserializeTypeAhead<IFilterValueBuildings, CityView>(
            this.filters,
            'city',
            this.cityService as IGetById
        );
        await this.filterService.deserializeTypeAhead<IFilterValueBuildings, StateView>(
            this.filters,
            'state',
            this.stateService as IGetById
        );
        await this.filterService.deserializeTypeAhead<IFilterValueBuildings, CountryView>(
            this.filters,
            'country',
            this.countryService as IGetById
        );
        this.filterService.deserializeBoolean<IFilterValueBuildings>(this.filters, 'includeArchived');
        await this.filterService.deserializeTypeAhead<IFilterValueBuildings, UserView>(
            this.filters,
            'salespersons',
            this.userService as IGetById,
            true
        );
    }
}
